import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Sparkles, CheckCircle } from "lucide-react";

const formSchema = z.object({
  firstName: z.string().trim().min(2, "First name must be at least 2 characters").max(50, "First name must be less than 50 characters"),
  lastName: z.string().trim().min(2, "Last name must be at least 2 characters").max(50, "Last name must be less than 50 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().min(10, "Phone number must be at least 10 digits").max(20, "Phone number must be less than 20 digits"),
  programInterest: z.string().min(1, "Please select a program"),
  specificProgram: z.string().optional(),
  marketingConsent: z.boolean().default(false),
}).refine((data) => {
  if (data.programInterest && !data.specificProgram) {
    return false;
  }
  return true;
}, {
  message: "Please select a specific program",
  path: ["specificProgram"],
});

const programsByType: Record<string, Array<{ value: string; label: string }>> = {
  certificate: [
    { value: "apologetics", label: "Certification in Apologetics" },
    { value: "pastoral-care", label: "Certification in Pastoral Care" },
    { value: "chaplain-studies", label: "Certification in Chaplain Studies" },
    { value: "educational-leadership-cert", label: "Certification in Christian Educational Leadership" },
    { value: "biblical-counseling", label: "Certification in Biblical Counseling" },
    { value: "evangelism", label: "Certification in Evangelism" },
    { value: "new-testament", label: "Certification in New Testament" },
    { value: "old-testament", label: "Certification in Old Testament" },
    { value: "spiritual-warfare", label: "Certification in Spiritual Warfare" },
    { value: "hebrew-greek-arabic", label: "Certification in Hebrew, Greek & Arabic" },
  ],
  associate: [
    { value: "biblical-studies-aa", label: "A.A. in Biblical Studies" },
    { value: "ministry-aa", label: "A.A. in Ministry" },
  ],
  bachelor: [
    { value: "biblical-studies-ba", label: "B.A. in Biblical Studies" },
    { value: "christian-counseling-ba", label: "B.A. in Christian Counseling" },
    { value: "christian-education-ba", label: "B.A. in Christian Education" },
    { value: "church-history-ba", label: "B.A. in Church History" },
    { value: "divinity-ba", label: "B.A. in Divinity" },
    { value: "ministry-ba", label: "B.A. in Ministry" },
    { value: "philosophy-ba", label: "B.A. in Christian Philosophy" },
    { value: "christian-psychology-ba", label: "B.A. in Christian Psychology" },
    { value: "theology-ba", label: "B.A. in Theology" },
  ],
  master: [
    { value: "biblical-studies-ma", label: "M.A. in Biblical Studies" },
    { value: "christian-counseling-ma", label: "M.A. in Christian Counseling" },
    { value: "christian-education-ma", label: "M.A. in Christian Education" },
    { value: "church-history-ma", label: "M.A. in Church History" },
    { value: "divinity-ma", label: "M.A. in Divinity" },
    { value: "ministry-ma", label: "M.A. in Ministry" },
    { value: "philosophy-ma", label: "M.A. in Christian Philosophy" },
    { value: "christian-psychology-ma", label: "M.A. in Christian Psychology" },
    { value: "theology-ma", label: "M.A. in Theology" },
  ],
  doctoral: [
    { value: "biblical-studies-dmin", label: "D.Min. in Biblical Studies" },
    { value: "christian-counseling-dmin", label: "D.Min. in Christian Counseling" },
    { value: "christian-education-dmin", label: "D.Min. in Christian Education" },
    { value: "church-history-dmin", label: "D.Min. in Church History" },
    { value: "divinity-dmin", label: "D.Min. in Divinity" },
    { value: "educational-leadership-dmin", label: "D.Min. in Christian Educational Leadership" },
    { value: "ministry-dmin", label: "D.Min. in Ministry" },
    { value: "philosophy-dmin", label: "D.Min. in Christian Philosophy" },
    { value: "christian-psychology-dmin", label: "D.Min. in Christian Psychology" },
    { value: "theology-dmin", label: "D.Min. in Theology" },
  ],
  phd: [
    { value: "biblical-studies-phd", label: "Ph.D. in Biblical Studies" },
    { value: "christian-counseling-phd", label: "Ph.D. in Christian Counseling" },
    { value: "christian-education-phd", label: "Ph.D. in Christian Education" },
    { value: "church-history-phd", label: "Ph.D. in Church History" },
    { value: "divinity-phd", label: "Ph.D. in Divinity" },
    { value: "educational-leadership-phd", label: "Ph.D. in Christian Educational Leadership" },
    { value: "ministry-phd", label: "Ph.D. in Ministry" },
    { value: "philosophy-phd", label: "Ph.D. in Christian Philosophy" },
    { value: "christian-psychology-phd", label: "Ph.D. in Christian Psychology" },
    { value: "theology-phd", label: "Ph.D. in Theology" },
  ],
};

type FormData = z.infer<typeof formSchema>;

const LeadCaptureForm = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      programInterest: "",
      specificProgram: "",
      marketingConsent: false,
    },
  });

  const selectedProgramInterest = form.watch("programInterest");
  const availablePrograms = selectedProgramInterest ? programsByType[selectedProgramInterest] || [] : [];

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);

    try {
      // Track form start event
      await supabase.from('events').insert({
        event_type: 'lead_form_submitted',
        metadata: { program: data.programInterest, specific_program: data.specificProgram },
      });

      const { data: result, error } = await supabase.functions.invoke('capture-lead', {
        body: data,
      });

      if (error) throw error;

      setIsSubmitted(true);
      toast({
        title: "Success!",
        description: "Thank you! Check your email for next steps.",
      });
    } catch (error) {
      console.error('Form submission error:', error);
      
      await supabase.from('events').insert({
        event_type: 'lead_form_error',
        metadata: { error: error.message },
      });

      toast({
        title: "Error",
        description: "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="glass rounded-lg p-8 shadow-strong border border-border/50 animate-scale-in">
        <div className="text-center space-y-4">
          <CheckCircle className="h-16 w-16 text-secondary mx-auto animate-scale-in" />
          <h3 className="text-2xl font-serif font-bold text-foreground">Thank You!</h3>
          <p className="text-muted-foreground">
            We've received your information and will contact you soon with next steps.
          </p>
          <p className="text-sm text-muted-foreground">
            Check your email for a confirmation message.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="glass rounded-lg p-8 shadow-strong border border-border/50 animate-fade-in">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-5 w-5 text-secondary animate-pulse" />
          <h3 className="text-2xl font-serif font-bold text-foreground">Start Your Journey Today</h3>
        </div>
        <p className="text-muted-foreground text-sm">
          Get personalized guidance from an admissions counselor
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 animate-in fade-in duration-500">
          <FormField
            control={form.control}
            name="firstName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>First Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="John" 
                    {...field} 
                    className="transition-smooth focus:scale-[1.02] focus:shadow-soft"
                  />
                </FormControl>
                <FormMessage className="animate-in slide-in-from-top-1 duration-300" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="lastName"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Last Name</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="Doe" 
                    {...field} 
                    className="transition-smooth focus:scale-[1.02] focus:shadow-soft"
                  />
                </FormControl>
                <FormMessage className="animate-in slide-in-from-top-1 duration-300" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input 
                    type="email" 
                    placeholder="john@example.com" 
                    {...field} 
                    className="transition-smooth focus:scale-[1.02] focus:shadow-soft"
                  />
                </FormControl>
                <FormMessage className="animate-in slide-in-from-top-1 duration-300" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="phone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Phone</FormLabel>
                <FormControl>
                  <Input 
                    type="tel" 
                    placeholder="(555) 123-4567" 
                    {...field} 
                    className="transition-smooth focus:scale-[1.02] focus:shadow-soft"
                  />
                </FormControl>
                <FormMessage className="animate-in slide-in-from-top-1 duration-300" />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="programInterest"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Program Interest</FormLabel>
                <Select 
                  onValueChange={(value) => {
                    field.onChange(value);
                    form.setValue("specificProgram", "");
                  }} 
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select a program level" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="certificate">Certificate Program</SelectItem>
                    <SelectItem value="associate">Associate Degree</SelectItem>
                    <SelectItem value="bachelor">Bachelor's Degree</SelectItem>
                    <SelectItem value="master">Master's Degree</SelectItem>
                    <SelectItem value="doctoral">Doctorate Degree</SelectItem>
                    <SelectItem value="phd">PhD Program</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {selectedProgramInterest && availablePrograms.length > 0 && (
            <FormField
              control={form.control}
              name="specificProgram"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Specific Degree Program</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a specific program" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-background border-border z-50">
                      {availablePrograms.map((program) => (
                        <SelectItem key={program.value} value={program.value}>
                          {program.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={form.control}
            name="marketingConsent"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel className="text-sm font-normal text-muted-foreground">
                    I agree to receive communications about programs and admissions
                  </FormLabel>
                </div>
              </FormItem>
            )}
          />

          <Button 
            type="submit" 
            className="w-full bg-secondary hover:bg-secondary-hover text-secondary-foreground font-semibold transition-smooth hover:scale-[1.02] hover:shadow-glow active:scale-95"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <span className="animate-spin">⏳</span> Submitting...
              </span>
            ) : (
              "Request Information"
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export default LeadCaptureForm;